// Curated user accents (Phase 4 of the product-theme architecture) — ids + PRODUCT POLICY only.
//
// The actual accent COLORS live in the kit, one per id, as `[data-accent="<id>"] .smx` rules
// generated from packages/ui/tokens/tokens.json. This module carries only (a) the id allowlist and
// (b) which products may offer personalization and which accents. accents.test.ts asserts ACCENT_IDS
// matches the kit's registry so the two cannot drift.
//
// An accent overrides ONLY the 7 brand-emphasis roles (accent/accentFill/accentText/accentHover/accentSubtle/
// onAccent/focusRing); it never touches semantic meaning (status/confidence/evidence/momentum/
// signal/dataviz). `null` = the default SignalMax orange. Accent availability is product-controlled:
// a product absent from PRODUCT_ACCENT_POLICY offers NO personalization and never stamps data-accent,
// so no other SignalMax app can acquire Careers' accent behavior. (Tenant/account-level branding and
// policy are Phase 5 — identity_account_branding — and are deliberately NOT built here.)

export const ACCENT_IDS = ["blue", "purple", "green", "pink", "neutral"] as const
export type AccentId = (typeof ACCENT_IDS)[number]

/** A concrete curated accent id (not null). */
export function isAccentId(v: unknown): v is AccentId {
  return typeof v === "string" && (ACCENT_IDS as readonly string[]).includes(v)
}

/** A storable accent value: a curated id, or null (= inherit the default orange). */
export function isAccentValue(v: unknown): v is AccentId | null {
  return v === null || isAccentId(v)
}

export type AccentPolicy = {
  personalization: boolean
  accents: readonly AccentId[]
  default: AccentId | null
}

// Static per-product policy for Phase 4. Careers is the first (and only) personalization-enabled
// product. Keep this minimal; Phase 5 relocates per-account overrides to tenant branding.
export const PRODUCT_ACCENT_POLICY: Record<string, AccentPolicy> = {
  careers: { personalization: true, accents: ACCENT_IDS, default: null },
}

export function accentPolicyFor(product: string | null | undefined): AccentPolicy | null {
  if (!product) return null
  return PRODUCT_ACCENT_POLICY[product] ?? null
}

/** The accent id to actually APPLY on a product surface, given the user's stored choice:
 *  the choice when the product's policy allows that id, otherwise the product's default,
 *  otherwise null (the default SignalMax orange). A product with no policy always yields null,
 *  so it can never render a personalized accent. */
export function resolveAccent(product: string | null | undefined, chosen: unknown): AccentId | null {
  const policy = accentPolicyFor(product)
  if (!policy || !policy.personalization) return null
  if (isAccentId(chosen) && policy.accents.includes(chosen)) return chosen
  return policy.default
}
